import React, { PureComponent } from 'react';
import styled from 'styled-components';
import { NotificationManager } from 'react-notifications';

import * as api from '../../lib/api';
import * as timeline from '../../lib/timeline';
import { withUser } from '../../lib/user';
import withUrlState from '../../lib/with-url-state';
import Screen from '../../components/Screen';
import Tabs from '../../components/Tabs';
import Button from '../../components/Button';
import Content from '../../components/Content';
import Icon from '../../components/Icon';
import SubHeader from '../../components/SubHeader';
import Placeholder from '../../components/Placeholder';
import Input from '../../components/Input';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Calendar from '../../components/Calendar';
import { T } from '../../components/I18n';

import ScheduledReportCard from './ScheduledReportCard';
import ScheduledReportFormModal from './ScheduledReportFormModal';
import SideModal from '../../components/SideModal';

class ScheduledReportsScreen extends PureComponent {
  state = {
    loading: true,
    error: false,
    schedules: [],
    reports: [],
    users: [],
    catalog: []
  };

  async componentDidMount() {
    const { credentials } = this.props;

    try {
      let [reports, users, catalog] = await Promise.all([
        api.getReports(credentials),
        api.getAllUsers(credentials),
        api.getCatalog(credentials)
      ]);

      // Only consider published reports.
      reports = reports.filter(report => report.published);

      // Load all of the schedules
      const schedules = await Promise.all(reports.map(report => api.getScheduledReports(report.id, credentials)));

      // Configure chip input.
      users.forEach(user => user.toString = () => user.userName);

      this.setState({ reports, users, catalog, schedules });
    }
    catch (e) {
      this.setState({ error: true });
    }
    finally {
      this.setState({ loading: false });
    }
  }

  handleReportFilterChange = e => {
    this.props.setUrlState({ report: e.target.value });
  }

  handleCalendarChange = ([year, month]) => {
    this.props.setUrlState({ year, month });
  }

  handleCloseScheduleModal = () => {
    const { location: { search }, history } = this.props;
    history.push(`/reports/schedules${search}`);
  }

  handleSaveSchedule = (report, schedule) => {
    this.setState(prevState => {
      const index = prevState.reports.map(report => report.id).indexOf(report.id);
      const schedules = [...prevState.schedules];
      schedules[index] = [...schedules[index].filter(s => s.id !== schedule.id), schedule];

      return { schedules };
    }, this.handleCloseScheduleModal);
  }

  handleDeleteSchedule = async (report, schedule, fromCalendarPill = false) => {
    const { credentials } = this.props;

    // eslint-disable-next-line
    if (fromCalendarPill && !confirm('Are your sure you want to delete this scheduled report?')) {
      return;
    }

    try {
      await api.deleteScheduledReport(report.id, schedule.id, credentials);
      NotificationManager.success('Your scheduled report was deleted');
      this.setState(prevState => ({
        schedules: prevState.schedules.map(schedulesForReport => schedulesForReport.filter(s => s.id !== schedule.id))
      }));
    }
    catch (e) {
      NotificationManager.error('Unable to delete your scheduled report. Please try again.');
    }
  }

  options(active) {
    const { urlState: { report } } = this.props;
    const { reports } = this.state;

    return [
      reports.length > 0 && (
        <ReportInput
          type="select"
          id="report-filter"
          label="Report:"
          labelPosition="start"
          value={report}
          onChange={this.handleReportFilterChange}>
          <option value="">All reports</option>
          {reports.map(report => (
            <option key={report.id} value={report.id}>{report.name}</option>
          ))}
        </ReportInput>
      ),
      <ViewButtonGroup>
        <Button active={active === 'list'} to={`/reports/schedules?view=list&report=${report}`}>
          <Icon name="list" /> This Week
        </Button>
        <Button active={active === 'calendar'} to={`/reports/schedules?view=calendar&report=${report}`}>
          <Icon name="calendar" /> Calendar
        </Button>
      </ViewButtonGroup>
    ];
  }

  renderListView() {
    const { urlState: { report } } = this.props;
    const { reports, schedules, users, catalog } = this.state;
    const reportByScheduleId = getReportsByScheduleId(reports, schedules);
    const allSchedules = getAllSchedules(schedules).filter(filterByReportId(report, reportByScheduleId));
    const weeklySchedules = timeline.getWeeklyEvents(allSchedules);
    const totalSchedulesThisWeek = weeklySchedules.reduce((count, day) => count + day.events.length, 0);
    const usersById = users.reduce((map, user) => {
      map[user.id] = user;
      return map;
    }, {});

    if (totalSchedulesThisWeek < 1) {
      return (
        <Content>
          <SubHeader options={this.options('list')}>This Week</SubHeader>
          <NoScheduledReports>There are no reports scheduled for this week.</NoScheduledReports>
        </Content>
      );
    }

    return (
      <Content>
        {weeklySchedules.filter(day => day.events.length > 0).map((day, i) => (
          <ScheduledReportSection key={day.date}>
            <SubHeader options={i === 0 && this.options('list')}>
              {timeline.getFriendlyDateName(day.date)}
            </SubHeader>
            {day.events.map(schedule => (
              <CardListContainer key={schedule.id}>
                <ScheduledReportCard
                  report={reportByScheduleId[schedule.id]}
                  schedule={schedule}
                  usersById={usersById}
                  catalog={catalog}
                  onDelete={this.handleDeleteSchedule}
                />
              </CardListContainer>
            ))}
          </ScheduledReportSection>
        ))}
      </Content>
    );
  }

  renderCalendarView() {
    const { urlState: { year: yearAsString, month: monthAsString, date: dateAsString, report }, location: { search } } = this.props;
    const { schedules, reports, users, catalog } = this.state;
    const year = parseInt(yearAsString, 10);
    const month = parseInt(monthAsString, 10);
    const reportByScheduleId = getReportsByScheduleId(reports, schedules);
    const allSchedules = getAllSchedules(schedules).filter(filterByReportId(report, reportByScheduleId));
    const selectedDate = dateAsString ? new Date(parseInt(dateAsString, 10)) : null;
    const usersById = users.reduce((map, user) => {
      map[user.id] = user;
      return map;
    }, {});

    return (
      <Content>
        <SubHeader options={this.options('calendar')} />
        <CalendarContainer>
          <Calendar year={year} month={month} height="calc(100vh - 26rem)" onChange={this.handleCalendarChange}>
            {date => {
              const schedulesForToday = timeline.getEventsForDate(date, allSchedules);

              return (
                <Calendar.Pills onMore={() => this.props.setUrlState({ date: date.getTime() })}>
                  {schedulesForToday.map(schedule => {
                    const report = reportByScheduleId[schedule.id];

                    return (
                      <Calendar.Pill
                        key={schedule.id}
                        to={`/reports/schedules/${schedule.id + search}`}
                        onDelete={() => this.handleDeleteSchedule(report, schedule, true)}>
                        {report.name}
                      </Calendar.Pill>
                    );
                  })}
                </Calendar.Pills>
              );
            }}
          </Calendar>
        </CalendarContainer>
        <SideModal
          open={selectedDate}
          title={selectedDate && timeline.getFriendlyDateName(selectedDate)}
          onClose={() => this.props.setUrlState({ date: null })}>
          {selectedDate && (
            <SideModal.Content>
              {timeline.getEventsForDate(selectedDate, allSchedules).map(schedule => (
                <CardCalendarContainer key={schedule.id}>
                  <ScheduledReportCard
                    report={reportByScheduleId[schedule.id]}
                    schedule={schedule}
                    usersById={usersById}
                    catalog={catalog}
                    onDelete={this.handleDeleteSchedule}
                  />
                </CardCalendarContainer>
              ))}
            </SideModal.Content>
          )}
        </SideModal>
      </Content>
    );
  }

  render() {
    const { urlState: { view }, match: { params: { id } }, location: { search } } = this.props;
    const { loading, error, reports, schedules, users, catalog } = this.state;
    const reportByScheduleId = getReportsByScheduleId(reports, schedules);
    const allSchedules = getAllSchedules(schedules);

    return (
      <Screen title={<T isolate>Scheduled Reports</T>}
        windowTitle="Scheduled Reports"
        activeLink="Reports"
        actions={reports.length > 0 && (<Button to={`/reports/schedules/create${search}`}>Schedule a Report</Button>)}
        tabs={
          <Tabs>
            <Tabs.Tab to="/reports">Reports</Tabs.Tab>
            <Tabs.Tab active to="/reports/schedules">Schedules</Tabs.Tab>
          </Tabs>
        }>
        {loading ? (
          <Loading fullScreen />
        ) : error ? (
          <Error fullScreen />
        ) : view === 'list' ? (
          this.renderListView()
        ) : (
                this.renderCalendarView()
              )}
        <ScheduledReportFormModal
          open={id && !loading && !error}
          catalog={catalog}
          report={id && reportByScheduleId[id]}
          schedule={allSchedules.filter(schedule => schedule.id === id)[0]}
          reports={reports}
          users={users}
          onSave={this.handleSaveSchedule}
          onClose={this.handleCloseScheduleModal}
        />
      </Screen>
    );
  }
}

function getReportsByScheduleId(reports, schedules) {
  return reports.reduce((map, report, i) => {
    schedules[i].forEach(schedule => {
      map[schedule.id] = report;
    });
    return map;
  }, {});
}

function getAllSchedules(schedules) {
  return schedules
    .reduce((flatMap, next) => flatMap.concat(next), [])
    .map(schedule => ({
      ...schedule,
      repeatInterval: schedule.interval
    }));
}

function filterByReportId(reportId, reportByScheduleId) {
  return schedule => reportId ? reportByScheduleId[schedule.id].id === reportId : true;
}

const ViewButtonGroup = styled(Button.Group)`
  @media (max-width: 50rem) {
    display: none;
  }
`;

const NoScheduledReports = styled(Placeholder)`
  height: 8rem;
  margin-top: 2rem;
`;

const ReportInput = styled(Input)`
  width: 15rem;

  @media (max-width: 50rem) {
    display: none;
  }
`;

const CalendarContainer = styled.div`
  margin-top: 1rem;
`;

const CardListContainer = styled.div`
  padding: 1rem 0;
`;

const CardCalendarContainer = styled.div`
  padding: 1rem 0;

  :first-child {
    padding-top: 0;
  }

  :last-child {
    padding-bottom: 0;
  }
`;

const ScheduledReportSection = styled.div`
  margin-bottom: 2rem;
  
  :last-child {
    margin-bottom: 0;
  }
`;

const defaultUrlState = {
  view: 'list',
  report: '',
  month: new Date().getMonth() + '',
  year: new Date().getFullYear() + ''
};

export default withUrlState(defaultUrlState)(withUser(ScheduledReportsScreen));